/**
 * Wallet loading + versioned-send helpers. Used by the CLI, relayer, and
 * any downstream consumer of @tonado/core that needs to drive a deposit or
 * a self-relayed withdraw from a user-controlled wallet.
 *
 * We support BOTH v4R2 and v5R1. v5R1 is the modern default (Tonkeeper,
 * ton-cli, TON Connect 2) and is preferred for new wallets. v4R2 remains
 * supported because many existing wallets are still at that version.
 *
 * The V5R1 `walletId` construction MUST include `networkGlobalId` and the
 * `context` discriminator — otherwise the derived address won't match what
 * Tonkeeper / ton-cli produce. Earlier versions of this file omitted those
 * fields; we replicate the canonical construction here.
 */
import { mnemonicToPrivateKey } from "@ton/crypto";
import { WalletContractV4, WalletContractV5R1, } from "@ton/ton";
import { SendMode } from "@ton/core";
/**
 * V5R1 wallet_id discriminator: encodes the chain (mainnet / testnet) so an
 * address derived for testnet can't be spent on mainnet (and vice versa).
 *   mainnet = -239 (the masterchain global_id used by mainnet)
 *   testnet = -3   (used by ton-cli + Tonkeeper Testnet)
 */
const NETWORK_GLOBAL_ID = {
    mainnet: -239,
    testnet: -3,
};
/**
 * Multi-version wallet loader. Default version is v5R1 (the modern,
 * recommended Tonkeeper/W5 default).
 *
 * For backward compatibility with code that passes only `{ mnemonic, client }`,
 * `network` defaults to "mainnet" — that's the right default for v5R1 production
 * use; if you call this for testnet without passing `network: "testnet"` you'll
 * get a different address than the same mnemonic would produce on testnet.
 * CLI/relayer callers always pass the active network explicitly.
 */
export async function loadWalletFromMnemonic(opts) {
    const words = opts.mnemonic.trim().split(/\s+/);
    if (words.length !== 24) {
        throw new Error(`Mnemonic must be 24 words, got ${words.length}.`);
    }
    const keyPair = await mnemonicToPrivateKey(words);
    const workchain = opts.workchain ?? 0;
    const version = opts.version ?? "v5R1";
    const network = opts.network ?? "mainnet";
    const sub = opts.subwalletNumber ?? 0;
    let wallet;
    if (version === "v4R2") {
        wallet = opts.client.open(WalletContractV4.create({
            workchain,
            publicKey: keyPair.publicKey,
            walletId: sub === 0 ? undefined : sub,
        }));
    }
    else {
        wallet = opts.client.open(WalletContractV5R1.create({
            publicKey: keyPair.publicKey,
            walletId: {
                networkGlobalId: NETWORK_GLOBAL_ID[network],
                context: {
                    workchain,
                    walletVersion: "v5r1",
                    subwalletNumber: sub,
                },
            },
        }));
    }
    return {
        wallet,
        keyPair,
        address: wallet.address.toString(),
        version,
    };
}
/**
 * Strict v4 loader. Preserved for callers that still need a typed V4 handle
 * (e.g. older code that pre-dates the version-agnostic deposit/withdraw
 * flows). New callers should prefer `loadWalletFromMnemonic`.
 */
export async function loadWalletV4FromMnemonic(opts) {
    const words = opts.mnemonic.trim().split(/\s+/);
    if (words.length !== 24) {
        throw new Error(`Mnemonic must be 24 words, got ${words.length}.`);
    }
    const keyPair = await mnemonicToPrivateKey(words);
    const wallet = opts.client.open(WalletContractV4.create({
        workchain: opts.workchain ?? 0,
        publicKey: keyPair.publicKey,
    }));
    return { wallet, keyPair, address: wallet.address.toString() };
}
/**
 * Version-agnostic `sendTransfer`. V5R1's `sendTransfer` signature requires
 * an explicit `authType` discriminator and a concrete `sendMode`; V4 doesn't
 * have either. Branch here so callers can stay uniform.
 *
 * Default `sendMode` is `PAY_GAS_SEPARATELY` (mode 1), which matches V4's
 * default and ensures the destination receives the full attached value
 * without forward-fee deduction.
 */
export async function sendTransferVersioned(loaded, args) {
    const sendMode = args.sendMode ?? SendMode.PAY_GAS_SEPARATELY;
    if (loaded.version === "v4R2") {
        const w = loaded.wallet;
        await w.sendTransfer({ ...args, sendMode });
        return;
    }
    const w = loaded.wallet;
    await w.sendTransfer({ ...args, sendMode, authType: "external" });
}
//# sourceMappingURL=walletLoader.js.map